import React, { useMemo, useState } from 'react';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    getFilteredRowModel,
    flexRender,
} from '@tanstack/react-table';
import KnoaToggle from '../Common/KnoaToggle';

const columnHelper = createColumnHelper();

const naturalSort = (rowA, rowB, columnId) => {
    const a = rowA.getValue(columnId);
    const b = rowB.getValue(columnId);

    const isNumericA = !isNaN(a);
    const isNumericB = !isNaN(b);

    if (isNumericA && isNumericB) {
        return Number(a) - Number(b);
    }
    if (isNumericA) return -1;
    if (isNumericB) return 1;

    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

const numericSort = (rowA, rowB, columnId) => {
    const a = rowA.getValue(columnId);
    const b = rowB.getValue(columnId);
    return Number(a) - Number(b);
};

export default function ReactTableWrapperTaskmining({
                                                        dataTable,
                                                        t,
                                                        handleToggleChange,
                                                        updateCurrentMapping,
                                                        editMode,
                                                        defaultPageSize = 25,
                                                    }) {

    const data = useMemo(() => dataTable, [dataTable]);

    const columns = useMemo(() => [
        columnHelper.accessor('url', {
            header: t('gtm:domain'),
            size: 290,
            enableColumnFilter: true,
            sortingFn: naturalSort,
            cell: info => <span>{info.getValue()}</span>
        }),

        columnHelper.accessor('name', {
            header: t('gtm:applicationName'),
            size: 290,
            enableColumnFilter: true,
            sortingFn: naturalSort,
            cell: info =>
                editMode ? (
                    <div
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={e => {
                            const newValue = e.target.innerText;
                            updateCurrentMapping(info.row.original.id, 'name', newValue);
                        }}
                        role="textbox"
                        aria-label={t('gtm:applicationName')}
                        className="editable-cell"
                    >
                        {info.getValue()}
                    </div>
                ) : (
                    info.getValue()
                ),
        }),

        columnHelper.accessor('category', {
            header: t('gtm:applicationCategory'),
            size: 290,
            enableColumnFilter: true,
            sortingFn: naturalSort,
            cell: info =>
                editMode ? (
                    <div
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={e => {
                            const newValue = e.target.innerText;
                            updateCurrentMapping(info.row.original.id, 'category', newValue);
                        }}
                        role="textbox"
                        aria-label={t('gtm:applicationCategory')}
                        className="editable-cell"
                    >
                        {info.getValue()}
                    </div>
                ) : (
                    info.getValue()
                ),
        }),
        columnHelper.accessor('appLevel', {
            header: t('gtm:enableMonitoring'),
            enableSorting: true,
            enableColumnFilter: true,
            size: 180,
            filterFn: (row, columnId, filterValue) => {
                const value = row.getValue(columnId);
                if (filterValue === 'Yes') return value === true;
                if (filterValue === 'No') return value === false;
                return true; // no filter applied
            },
            cell: info => (
                <div className="centerContainer">
                    <KnoaToggle
                        Name={`${info.row.original.id}:appLevel`}
                        onChange={e => handleToggleChange(e, `${info.row.original.id}:appLevel`)}
                        value={info.getValue()}
                        labelId={`${info.row.original.id}:appLevel`}
                        disabled={!editMode}
                        onText={t('common:yes')}
                        offText={t('common:no')}
                    />
                </div>
            ),
        }),
        columnHelper.accessor('users', {
            header: t('gtm:uniqueUsers'),
            size: 150,
            sortingFn: numericSort,
            cell: info => (
                <div className="centerContainer">
                    {info.getValue()}
                </div>
            ),
        }),
    ], [t, handleToggleChange, updateCurrentMapping, editMode]);

    const [sorting, setSorting] = useState([{ id: 'users', desc: true }]);
    const [columnFilters, setColumnFilters] = useState([]);

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            columnFilters,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        enableMultiSort: true,
        initialState: { pagination: { pageSize: defaultPageSize } },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    return (
        <>
            <div className="table-responsive">
                <table className="table table-hover -highlight" role="table">
                    <thead>
                    {/* Header Row with Sorting */}
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                                <th
                                    key={header.id}
                                    onClick={() => {
                                        if (!header.column.getCanSort()) return;
                                        const isSorted = header.column.getIsSorted();
                                        const id = header.column.id;
                                        setSorting([{ id, desc: isSorted === 'asc' }]);
                                    }}
                                    style={{
                                        cursor: header.column.getCanSort() ? 'pointer' : 'default',
                                        textAlign: header.column.columnDef.size <= 150 ? 'center' : 'left',
                                        whiteSpace: 'nowrap'
                                    }}
                                    scope="col"
                                    aria-label={`Sort by ${header.column.columnDef.header}`}
                                    aria-sort={
                                        header.column.getIsSorted()
                                            ? header.column.getIsSorted() === 'desc'
                                                ? 'descending'
                                                : 'ascending'
                                            : 'none'
                                    }
                                    tabIndex={header.column.getCanSort() ? 0 : -1}
                                    onKeyDown={e => {
                                        if ((e.key === 'Enter' || e.key === ' ') && header.column.getCanSort()) {
                                            const isSorted = header.column.getIsSorted();
                                            setSorting([{ id: header.column.id, desc: isSorted === 'asc' }]);
                                        }
                                    }}
                                >
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                    {header.column.getCanSort() && (
                                        <span className={`sort-icon-stack ${header.column.getIsSorted() ? 'sorted' : ''}`}>
                                            {header.column.getIsSorted() === 'asc' && <i className="fa fa-sort-up" />}
                                            {header.column.getIsSorted() === 'desc' && <i className="fa fa-sort-down" />}
                                            {header.column.getIsSorted() === false && (
                                                <>
                                                    <i className="fa fa-sort-up" />
                                                    <i className="fa fa-sort-down" />
                                                </>
                                            )}
                                        </span>
                                    )}
                                </th>
                            ))}
                        </tr>
                    ))}

                    <tr>
                        {table.getHeaderGroups()[0].headers.map(header => {
                            const column = header.column;
                            const id = column.id;

                            const showTextFilter = ['url', 'name', 'category'].includes(id);
                            const showDropdownFilter = id === 'appLevel';

                            return (
                                <th key={`filter-${id}`}>
                                    {showTextFilter && (
                                        <input
                                            type="text"
                                            value={column.getFilterValue() ?? ''}
                                            onChange={e => column.setFilterValue(e.target.value)}
                                            placeholder={t('common:typeToSearch')}
                                            className="form-control form-control-sm"
                                            aria-label={`Filter ${column.columnDef.header}`}
                                        />
                                    )}

                                    {showDropdownFilter && (
                                        <select
                                            value={column.getFilterValue() ?? ''}
                                            onChange={e => column.setFilterValue(e.target.value || undefined)}
                                            className="form-select form-select-sm table-filter-select"
                                            aria-label={`Filter ${column.columnDef.header}`}
                                        >
                                            <option value="">All</option>
                                            <option value="Yes">Yes</option>
                                            <option value="No">No</option>
                                        </select>
                                    )}
                                </th>
                            );
                        })}
                    </tr>
                    </thead>

                    {/* Table Body */}
                    <tbody>
                    {table.getRowModel().rows.length === 0 ? (
                        <tr>
                            <td className="text-center align-middle" colSpan={columns.length}>{t('common:noRowsFound')}</td>
                        </tr>
                    ) : (
                        table.getRowModel().rows.map(row => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map(cell => (
                                    <td key={cell.id} style={{ width: `${cell.column.getSize()}px` }}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            <div className="pagination-controls">
                <button type="button" className="page-button pb-s" onClick={() => table.firstPage()} disabled={!table.getCanPreviousPage()}>
                    {'<<'}
                </button>
                <button type="button" className="page-button pb-l" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                    {t('common:previous')}
                </button>
                <span className="flex items-center gap-1 page-span">
                    {t('common:page')}{' '}
                    <input
                        type="number"
                        min={1}
                        max={table.getPageCount()}
                        value={table.getState().pagination.pageIndex + 1}
                        onChange={e => {
                            const page = e.target.value ? Number(e.target.value) - 1 : 0;
                            table.setPageIndex(page);
                        }}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault(); // stops form submission
                            }
                        }}
                        className="page-input"
                    />{' '}
                    {t('common:of')} {table.getPageCount()}
                </span>
                <button type="button" className="page-button pb-l" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                    {t('common:next')}
                </button>
                <button type="button" className="page-button pb-s" onClick={() => table.lastPage()} disabled={!table.getCanNextPage()}>
                    {'>>'}
                </button>
                <select
                    className="page-select"
                    style={{ fontSize: '12px' }}
                    value={table.getState().pagination.pageSize}
                    onChange={e => table.setPageSize(Number(e.target.value))}
                >
                    {[25, 50, 100].map(size => (
                        <option key={size} value={size}>
                            {size} Rows
                        </option>
                    ))}
                </select>
            </div>
        </>
    );
}